import { VideoData } from '@lumen5/luminary';
import type { Scene } from '@lumen5/luminary';
import { TOOL_NAMES } from 'js/src/agent/toolNames';
import { z } from 'zod';

import {
    type GraphicRunSetup,
    graphicSceneProblem,
    prepareGraphicRun,
    removeUndesignedGraphic,
} from '../html-design/graphic/graphicRun';
import type { FrameBox } from '../html-design/graphic/speakerFaces';
import { isLayeredGraphicScene } from '../html-design/layeredGraphic';
import type { SubagentRegistry, SubagentRun } from '../subagents/SubagentRegistry';
import { SubagentStatus } from '../subagents/SubagentRegistry';
import { SubagentAccess } from '../subagents/subagentAccess';
import { SubagentScope } from '../subagents/SubagentScope';
import {
    DEFAULT_SUBAGENT_TYPE,
    designsOneScene,
    getSubagentModelSettings,
    SUBAGENT_TYPES,
} from '../subagents/subagentTypes';
import type { SubagentType } from '../subagents/subagentTypes';
import type { EnhancedToolResult, MessageContent, ToolCall, ToolExecutionContext } from '../types';
import { isAssistantMessage, isToolMessage } from '../types';
import type { VideoDiff, VideoStateManager, VideoStateSnapshot } from '../video-state';
import {
    captureVideoState,
    computeVideoDiff,
    filterVideoDiffToSceneGroups,
    filterVideoDiffToScenes,
    formatVideoDiffAsGitPatch,
} from '../video-state';

import { SKIPPED_AT_CAP_RESULT } from '../ToolExecutor';
import { ConversationTool } from './ConversationTool';
import { MOTION_GRAPHICS_SCENE_DESIGN_ID } from './motion-graphics/motionGraphicsSceneDesign';
import { resolveSceneById, sceneDisplayId } from './scene-addressing';
import { resolveSceneGroupById, sceneGroupDisplayId } from './scene-group-addressing';
import type { Tool, ToolConstructorArgs } from './Tool';

import type { Conversation } from 'js/src/agent';
import { isSceneDurationUserOverridden } from 'js/src/data/user-overrides';
import { ErrorReporter } from 'js/src/libs/error-reporter';
import type { HtmlAsset } from 'js/src/luminary/html-asset-operations';
import { isVoicedrivenScene } from 'js/src/luminary/voicedriven-scenes';

export const runSubagentSchema = z.object({
    description: z
        .string()
        .describe('A short (3-5 word) label for the task, shown to anyone watching the run.'),
    prompt: z
        .string()
        .describe(
            'The complete task. The subagent sees none of this conversation, so include every detail it needs, and say what it should report back.',
        ),
    type: z
        .enum(SUBAGENT_TYPES)
        .optional()
        .describe(
            '`general` (default): a copy of you, on your model. `codeo`: for motion-graphics and HTML composition work. `chart`: designs the one chart scene in scene_ids, from the figures you give it. `graphic`: designs the one graphic layered over the scene in scene_ids (an overlay of a talking head, footage or a stock design), from your brief.',
        ),
    scene_group_ids: z
        .array(z.string())
        .optional()
        .describe(
            'Required when you start more than one subagent in the same message, except for `chart` and `graphic`: the scene groups this subagent alone may edit, by scene_group_id. Scene groups it adds become its own. Subagents started together must not share a scene group. Pass [] for one that only adds new scene groups. Ignored when you start a single subagent, which may edit anything.',
        ),
    scene_ids: z
        .array(z.string())
        .optional()
        .describe(
            'For `chart` and `graphic`, and required for them: the one scene it designs, by scene_id. Each scene gets its own subagent, even over the same voiceover.',
        ),
});
export type RunSubagentArgs = z.infer<typeof runSubagentSchema>;

/** What a subagent that designs one scene is told about it, beyond what it reads from the scene. */
export interface DesignedScene {
    sceneIds: string[];
    /** A graphic's: where the speaker's head is, or null when detection couldn't say. */
    speakerHeads: FrameBox[] | null;
}

export interface RunSubagentToolArgs extends ToolConstructorArgs {
    getRegistry: () => SubagentRegistry;
    /** The kinds this user may start. Defaults to every kind. */
    allowedTypes?: readonly SubagentType[];
    /**
     * A fresh tool set per subagent: a Conversation attaches the tools it's given to itself. A
     * chart's or graphic's scene is the one scene its tools may write.
     */
    buildSubagentTools: (type: SubagentType, scene: DesignedScene) => Tool[];
    /** A chart's or graphic's scene is the scene its prompt describes. */
    getSubagentSystemMessage: (type: SubagentType, scene: DesignedScene) => string;
}

const SUBAGENT_MAX_ITERATIONS = 100;
// A chart or graphic writes its scene once, and once more only if that write fails its checks; its
// tool ends the run at the second failed write, so this is a backstop.
const ONE_SCENE_MAX_ITERATIONS = 2;

/** How a one-scene subagent's work is named in what it reports. */
const DESIGNED_WORK: Record<'chart' | 'graphic', string> = {
    chart: 'the chart scene',
    graphic: 'the graphic',
};

const SUBAGENT_PREAMBLE = `
## You are a subagent
Another instance of this assistant started you to do one task, given in the user message. The user can't see this conversation, so decide for yourself wherever you reasonably can. Ask them (\`${TOOL_NAMES.askUserQuestion}\`) only for a decision you truly can't make: your work pauses until they answer, and they'll see only your question, not this conversation. Don't review how scenes look: you don't have \`${TOOL_NAMES.reviewSceneImages}\`, and the agent that started you reviews the video after you report, so skip any step that asks for it. When you're done, reply with a concise report of what you did and anything the agent that started you needs to know. That reply is all it sees.`;

function scopedPreamble(scope: SubagentScope): string {
    return `
## Your part of the video
Other subagents are editing this video at the same time as you. You may edit only these scene groups: ${scope.describe()}, plus any scene groups you add. Tools refuse edits anywhere else, and video-wide settings (music, aspect ratio, template colors, color grading, captions, brand kit) are off limits. The video state you see shows only your scene groups. If something outside your part needs to change, say so in your report.

## Finish your part before reporting
Your part should need no cleanup from the agent that started you. Once it's built, run \`${TOOL_NAMES.lintVideo}\` (it reports only your scene groups), fix every error it finds, and repeat until it passes. Report only what you couldn't fix.`;
}

/** What one run_subagent call claims among the calls started with it: scene groups, or a chart's scene. */
interface Claim {
    sceneGroupIds: string[];
    sceneIds: string[];
}

interface SubagentBatch {
    readonly startSnapshot: Promise<VideoStateSnapshot>;
    readonly finishedCallIds: Set<string>;
}

function failure(result: string): EnhancedToolResult {
    return { result, isSuccess: false };
}

export class RunSubagentTool extends ConversationTool<
    typeof runSubagentSchema,
    RunSubagentToolArgs
> {
    protected override readonly schema = runSubagentSchema;
    protected override readonly toolName = TOOL_NAMES.runSubagent;
    public override readonly subagentAccess = SubagentAccess.UNAVAILABLE;
    // A getter: the limit belongs to the root agent's registry, which isn't set while the root
    // agent's constructor builds its tools.
    protected override get toolDescription(): string {
        return `Start a subagent: a copy of you that works through one self-contained task with the same tools, then reports back. A single subagent may edit anything. Several calls in one message (up to ${this.getRegistry().getMaxPerMessage()}) run in parallel, a few at a time with the rest waiting their turn, and share the video, so each must be given its own scene groups (scene_group_ids) and can't touch anyone else's or change video-wide settings. Split work by scene group, and make video-wide changes (template, aspect ratio, music, voice, colors) yourself, before or after.`;
    }

    public override canRunAsync = true;
    // Parallel subagents share one video, so a whole-video diff would credit each with its
    // siblings' edits. Each result shows only its own part instead.
    protected override readonly appendsVideoDiff = false;
    private readonly batchesByFirstCallId = new Map<string, SubagentBatch>();
    /** What each batched call's result diff is cut to: its scene groups, or a chart's scene. */
    private readonly claimsByCallId = new Map<string, Claim>();
    private readonly scopesByCallId = new Map<string, SubagentScope>();

    private readonly getRegistry: () => SubagentRegistry;
    private readonly allowedTypes: readonly SubagentType[];
    private readonly buildSubagentTools: RunSubagentToolArgs['buildSubagentTools'];
    private readonly getSubagentSystemMessage: RunSubagentToolArgs['getSubagentSystemMessage'];

    public constructor(args: RunSubagentToolArgs) {
        super(args);
        this.getRegistry = args.getRegistry;
        this.allowedTypes = args.allowedTypes ?? SUBAGENT_TYPES;
        this.buildSubagentTools = args.buildSubagentTools;
        this.getSubagentSystemMessage = args.getSubagentSystemMessage;
    }

    public override getToolDisplayName(toolCallArgs?: string): string {
        return toolCallArgs
            ? `Subagent: ${this.getDescription(toolCallArgs)}`
            : 'Running a subagent';
    }

    public get statusIcon(): string {
        return 'hub';
    }

    public override async executeWithValidation(
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        args: Record<string, any>,
        context: ToolExecutionContext,
    ): Promise<EnhancedToolResult> {
        const videoStateManager = context.videoStateManager;
        const batchCalls = this.getBatchCalls(context.toolCallId);
        if (!videoStateManager || batchCalls.length === 1) {
            const result = await super.executeWithValidation(args, context);
            if (videoStateManager) {
                await this.appendDiffSinceLastSnapshot(result, videoStateManager, args);
            }
            return result;
        }

        const batchKey = batchCalls[0].id;
        const batch = this.joinBatch(batchKey, videoStateManager);
        const result = await super.executeWithValidation(args, context);
        const claim = this.claimsByCallId.get(context.toolCallId);
        const scope = this.scopesByCallId.get(context.toolCallId);
        this.claimsByCallId.delete(context.toolCallId);
        this.scopesByCallId.delete(context.toolCallId);

        try {
            const currentSnapshot = await this.captureSnapshot(videoStateManager);
            if (claim) {
                const startSnapshot = await batch.startSnapshot;
                const diffText = formatVideoDiffAsGitPatch(
                    ownPartOf(
                        computeVideoDiff(startSnapshot, currentSnapshot),
                        startSnapshot,
                        currentSnapshot,
                        // The scope also holds the scene groups the subagent added.
                        scope ? { ...claim, sceneGroupIds: scope.getSceneGroupIds() } : claim,
                    ),
                    undefined,
                    videoStateManager.getSceneAddressing(),
                );
                if (diffText) {
                    result.result = `${result.result}\n\n${diffText}`;
                }
            }
            batch.finishedCallIds.add(context.toolCallId);
            if (batch.finishedCallIds.size === batchCalls.length) {
                this.batchesByFirstCallId.delete(batchKey);
                videoStateManager.setSnapshot(currentSnapshot);
            }
        } catch (error) {
            ErrorReporter.capture(error, { fingerprint: ['run-subagent-diff-failed'] });
            const reason = error instanceof Error ? error.message : String(error);
            result.result = `${result.result}\n\nNote: the subagent's edits could not be shown as a video-state diff (${reason}). Read the video state to see them.`;
        }
        return result;
    }

    private joinBatch(batchKey: string, videoStateManager: VideoStateManager): SubagentBatch {
        const existing = this.batchesByFirstCallId.get(batchKey);
        if (existing) {
            return existing;
        }
        const lastSnapshot = videoStateManager.getLastSnapshot();
        const batch: SubagentBatch = {
            startSnapshot: lastSnapshot
                ? Promise.resolve(lastSnapshot)
                : this.captureSnapshot(videoStateManager),
            finishedCallIds: new Set(),
        };
        this.batchesByFirstCallId.set(batchKey, batch);
        return batch;
    }

    private captureSnapshot(videoStateManager: VideoStateManager): Promise<VideoStateSnapshot> {
        return captureVideoState(
            this.luminary,
            undefined,
            videoStateManager.getStateAccessor(),
            videoStateManager.getIncludeColorNames(),
        );
    }

    /**
     * What this call claims among several started together, or why it can't. A chart or graphic
     * claims its scene; any other kind its scene groups. Where two calls claim the same scene or
     * scene group, the earlier call in the message keeps it.
     */
    private claimInBatch(
        type: SubagentType,
        { scene_group_ids, scene_ids }: Pick<RunSubagentArgs, 'scene_group_ids' | 'scene_ids'>,
        toolCallId: string,
        batchCalls: ToolCall[],
    ): Claim | string {
        const claim = designsOneScene(type)
            ? this.claimDesignedScene(type, scene_ids)
            : this.claimSceneGroups(scene_group_ids, batchCalls.length);
        if (typeof claim === 'string') {
            return claim;
        }
        const earlierCalls = batchCalls.slice(
            0,
            batchCalls.findIndex((toolCall) => toolCall.id === toolCallId),
        );
        for (const earlierCall of earlierCalls) {
            const clash = this.findClash(claim, this.parseClaim(earlierCall.function.arguments));
            if (clash) {
                return `${clash} is already given to "${this.getDescription(earlierCall.function.arguments)}", started earlier in the same message. Subagents started together can't share a scene or scene group.`;
            }
        }
        return claim;
    }

    /**
     * A chart's or graphic's scene, or why it can't have it. Each writes one scene, and its run ends
     * at its first write that passes its checks, so it's given exactly one: a chart a scene that is a
     * graphic of its own, a graphic one with something beneath to be layered over. `skipUnresolved`
     * reads an earlier call's claim, whose bad ids its own call reports.
     */
    private claimDesignedScene(
        type: SubagentType,
        sceneIds: string[] | undefined,
        { skipUnresolved = false } = {},
    ): Claim | string {
        if (sceneIds?.length !== 1) {
            return `a \`${type}\` subagent designs one scene, so pass its scene_id in scene_ids. Start one ${type} subagent per scene, all in the same message.`;
        }
        const resolved = resolveSceneById(this.luminary, this.getState(), sceneIds[0]);
        if ('error' in resolved) {
            return skipUnresolved ? { sceneGroupIds: [], sceneIds: [] } : resolved.error;
        }
        const { scene } = resolved;
        const problem =
            type === 'graphic'
                ? graphicSceneProblem(this.luminary, this.getState(), scene)
                : this.chartSceneProblem(scene);
        return problem ?? { sceneGroupIds: [], sceneIds: [scene.id] };
    }

    private chartSceneProblem(scene: Scene): string | null {
        if (
            scene.getAssetsByType(VideoData.HTML_ASSET_TYPE).length === 0 ||
            isLayeredGraphicScene(scene)
        ) {
            return `scene ${sceneDisplayId(this.luminary, scene.id)} isn't a chart scene. Add one first with ${TOOL_NAMES.addScene} or ${TOOL_NAMES.addVoiceoverScene} using scene_design_id "${MOTION_GRAPHICS_SCENE_DESIGN_ID}", then start the chart on that.`;
        }
        return null;
    }

    private claimSceneGroups(
        sceneGroupIds: string[] | undefined,
        batchSize: number,
        { skipUnresolved = false } = {},
    ): Claim | string {
        if (sceneGroupIds === undefined) {
            return `you started ${batchSize} subagents in one message, so each needs scene_group_ids, the scene groups it alone may edit (pass [] for one that only adds new scene groups). Start them again with scene_group_ids, or start just one, which may edit anything.`;
        }
        const resolved = sceneGroupIds.map((id) => resolveSceneGroupById(this.luminary, id));
        const unresolved = resolved.find((resolution) => 'error' in resolution);
        if (!skipUnresolved && unresolved && 'error' in unresolved) {
            return unresolved.error;
        }
        return {
            sceneGroupIds: resolved.flatMap((resolution) =>
                'error' in resolution ? [] : [resolution.sceneGroup.id],
            ),
            sceneIds: [],
        };
    }

    /** What an earlier call in the message claimed, read from its arguments; ids that don't resolve are skipped. */
    private parseClaim(toolCallArgs: string): Claim {
        const none: Claim = { sceneGroupIds: [], sceneIds: [] };
        try {
            const args = JSON.parse(toolCallArgs) as Partial<RunSubagentArgs>;
            const type = args.type ?? DEFAULT_SUBAGENT_TYPE;
            const claim = designsOneScene(type)
                ? this.claimDesignedScene(type, args.scene_ids, { skipUnresolved: true })
                : this.claimSceneGroups(args.scene_group_ids ?? [], 1, {
                      skipUnresolved: true,
                  });
            return typeof claim === 'string' ? none : claim;
        } catch {
            return none;
        }
    }

    /** The first scene or scene group both claims reach, named for the model, or null. */
    private findClash(own: Claim, earlier: Claim): string | null {
        const groupOf = (sceneId: string) =>
            this.luminary.scenes.find((scene) => scene.id === sceneId)?.sceneGroup?.id;
        const sharedScene = own.sceneIds.find((id) => earlier.sceneIds.includes(id));
        if (sharedScene) {
            return `scene ${sceneDisplayId(this.luminary, sharedScene)}`;
        }
        // A whole scene group clashes with the same group, or with a designed scene inside it.
        const reaches = (claim: Claim, sceneGroupId: string) =>
            claim.sceneGroupIds.includes(sceneGroupId) ||
            claim.sceneIds.some((id) => groupOf(id) === sceneGroupId);
        const sharedGroup =
            own.sceneGroupIds.find((id) => reaches(earlier, id)) ??
            earlier.sceneGroupIds.find((id) => reaches(own, id));
        return sharedGroup
            ? `scene group ${sceneGroupDisplayId(this.luminary, sharedGroup)}`
            : null;
    }

    private getDescription(toolCallArgs: string): string {
        try {
            return (JSON.parse(toolCallArgs) as Partial<RunSubagentArgs>).description ?? 'subagent';
        } catch {
            return 'subagent';
        }
    }

    /** Every call to this tool in the assistant message that made this call. */
    private getBatchCalls(toolCallId: string): ToolCall[] {
        const message = this._parentConversation
            ?.getMessages()
            .findLast(
                (candidate) =>
                    isAssistantMessage(candidate) &&
                    candidate.tool_calls?.some((toolCall) => toolCall.id === toolCallId),
            );
        const batchCalls =
            message && isAssistantMessage(message)
                ? message.tool_calls?.filter(
                      (toolCall) => toolCall.function.name === TOOL_NAMES.runSubagent,
                  )
                : undefined;
        return batchCalls?.length
            ? batchCalls
            : [
                  {
                      id: toolCallId,
                      type: 'function',
                      function: { name: TOOL_NAMES.runSubagent, arguments: '{}' },
                  },
              ];
    }

    /**
     * A chart is paced to its scene's length, so a new chart on a scene of its own needs that length
     * set first, with set_scene_duration; one over a voiceover lasts as long as its words, and one
     * being revised keeps its own. Why it can't start yet, or null.
     */
    private checkChartSceneLength(sceneId: string): string | null {
        const scene = this.luminary.getSceneById(sceneId);
        if (
            !scene ||
            isVoicedrivenScene(scene) ||
            isDesignedChartScene(scene) ||
            isSceneDurationUserOverridden(this.getState(), sceneId)
        ) {
            return null;
        }
        return `scene ${sceneDisplayId(this.luminary, sceneId)} isn't over a voiceover, so its length is yours to set, and the chart is paced to it. Set it first with ${TOOL_NAMES.setSceneDuration}, then start the chart.`;
    }

    /** What this call may work on, when that's limited: a designed scene, or a batched call's part. */
    private claimFor(
        type: SubagentType,
        args: Pick<RunSubagentArgs, 'scene_group_ids' | 'scene_ids'>,
        toolCallId: string,
        batchCalls: ToolCall[],
    ): Claim | string | undefined {
        if (batchCalls.length > 1) {
            return this.claimInBatch(type, args, toolCallId, batchCalls);
        }
        return designsOneScene(type) ? this.claimDesignedScene(type, args.scene_ids) : undefined;
    }

    public async execute(
        {
            description,
            prompt,
            type = DEFAULT_SUBAGENT_TYPE,
            scene_group_ids,
            scene_ids,
        }: RunSubagentArgs,
        context: ToolExecutionContext,
    ): Promise<EnhancedToolResult> {
        const parent = this._parentConversation;
        if (!parent) {
            throw new Error(`${TOOL_NAMES.runSubagent} ran without a parent conversation`);
        }
        if (parent.getParent()) {
            return failure(
                `Subagents can't start subagents of their own. Do this work yourself instead of calling ${TOOL_NAMES.runSubagent}.`,
            );
        }
        if (context.abortSignal?.aborted) {
            return failure('Cancelled before the subagent started.');
        }
        if (!this.allowedTypes.includes(type)) {
            const offered = this.allowedTypes.map((allowed) => `\`${allowed}\``).join(' or ');
            return failure(
                `Subagent not started: type \`${type}\` isn't available. Start a ${offered} subagent instead.`,
            );
        }

        const registry = this.getRegistry();
        const batchCalls = this.getBatchCalls(context.toolCallId);
        const positionInMessage = batchCalls.findIndex((call) => call.id === context.toolCallId);
        const maxPerMessage = registry.getMaxPerMessage();
        if (positionInMessage >= maxPerMessage) {
            return failure(
                `Subagent not started: at most ${maxPerMessage} subagents can be started in one message, and this was call ${positionInMessage + 1}. Start the rest in your next message, once these have reported back.`,
            );
        }

        const isBatch = batchCalls.length > 1;
        const claim = this.claimFor(
            type,
            { scene_group_ids, scene_ids },
            context.toolCallId,
            batchCalls,
        );
        if (typeof claim === 'string') {
            return failure(`Subagent not started: ${claim}`);
        }
        const designedSceneIds = designsOneScene(type) ? (claim?.sceneIds ?? []) : [];
        const unsized =
            type === 'chart' && designedSceneIds.length
                ? this.checkChartSceneLength(designedSceneIds[0])
                : null;
        if (unsized) {
            return failure(`Subagent not started: ${unsized}`);
        }
        if (claim && isBatch) {
            this.claimsByCallId.set(context.toolCallId, claim);
        }

        const graphicScene =
            type === 'graphic' ? this.luminary.getSceneById(designedSceneIds[0]) : undefined;
        let graphic: GraphicRunSetup | null = null;
        if (graphicScene) {
            const prepared = await prepareGraphicRun(
                this.luminary,
                this.dispatch,
                this.getState,
                graphicScene,
            );
            if ('error' in prepared) {
                return failure(`Subagent not started: ${prepared.error}`);
            }
            graphic = prepared;
        }
        const result = await this.startSubagent({
            parent,
            registry,
            context,
            type,
            description,
            prompt,
            isBatch,
            claim,
            scene: { sceneIds: designedSceneIds, speakerHeads: graphic?.heads ?? null },
            still: graphic?.still ?? null,
        });
        if (graphicScene && graphic?.addedGraphic && !result.isSuccess) {
            removeUndesignedGraphic(
                this.luminary,
                this.dispatch,
                this.getState,
                graphicScene,
                context.parentActionGroup,
            );
        }
        return result;
    }

    private async startSubagent({
        parent,
        registry,
        context,
        type,
        description,
        prompt,
        isBatch,
        claim,
        scene,
        still,
    }: {
        parent: Conversation;
        registry: SubagentRegistry;
        context: ToolExecutionContext;
        type: SubagentType;
        description: string;
        prompt: string;
        isBatch: boolean;
        claim: Claim | undefined;
        scene: DesignedScene;
        still: string | null;
    }): Promise<EnhancedToolResult> {
        const designsScene = designsOneScene(type);
        // A chart's or graphic's one tool is bound to its scene, so it needs no scope; any other
        // subagent started with others may edit only the scene groups it claimed.
        const scope =
            claim && isBatch && !designsScene
                ? new SubagentScope(this.luminary, this.getState, claim.sceneGroupIds, {
                      capture: registry.captureQueue,
                      question: registry.questionQueue,
                  })
                : undefined;
        if (scope) {
            this.scopesByCallId.set(context.toolCallId, scope);
        }
        const builtTools = this.buildSubagentTools(type, scene);
        const tools = scope
            ? builtTools.filter((tool) => tool.subagentAccess !== SubagentAccess.BLOCKED)
            : builtTools;

        const ownPrompt = this.getSubagentSystemMessage(type, scene);
        const parentSettings = parent.getCloneableSettings();
        const { model, reasoningEffort } = getSubagentModelSettings(type, parentSettings);
        const child = this.createChildConversationForToolCall(context.toolCallId, {
            // A designed scene's own prompt is its whole instruction: it doesn't report, ask or lint.
            systemMessage: designsScene
                ? ownPrompt
                : `${ownPrompt}\n${SUBAGENT_PREAMBLE}${scope ? `\n${scopedPreamble(scope)}` : ''}`,
            tools,
            maxIterations: designsScene ? ONE_SCENE_MAX_ITERATIONS : SUBAGENT_MAX_ITERATIONS,
            task: `${parent.getTask()}_subagent_${type}`,
            // A designed scene is given the facts it needs about the video in its prompt instead.
            requiresVideoState: !designsScene && parentSettings.requiresVideoState,
            streaming: parentSettings.streaming,
            model,
            reasoningEffort,
            sceneAddressing: parentSettings.sceneAddressing,
            includeColorNames: parentSettings.includeColorNames,
            enableThinkingSummaries: parentSettings.enableThinkingSummaries,
            subagentScope: scope,
            subagentDescription: description,
        });
        const { run, admitted } = registry.admit(
            { description, type, conversation: child },
            context.abortSignal,
        );

        const opening: MessageContent = [
            { type: 'text', text: prompt },
            ...(still
                ? [
                      { type: 'text' as const, text: 'The scene as it is now:' },
                      { type: 'image_url' as const, image_url: { url: still } },
                  ]
                : []),
        ];
        try {
            const result = (await admitted)
                ? await this.runToCompletion(run, opening, registry, context, type)
                : this.neverStartedResult(run, registry);
            registry.finish(
                run.id,
                result.isSuccess ? SubagentStatus.COMPLETED : SubagentStatus.FAILED,
            );
            return result;
        } catch (error) {
            registry.finish(run.id, SubagentStatus.FAILED);
            throw error;
        }
    }

    private neverStartedResult(run: SubagentRun, registry: SubagentRegistry): EnhancedToolResult {
        return registry.getRun(run.id)?.killRequested
            ? failure('A developer stopped this subagent before it started. It made no changes.')
            : failure('Cancelled before the subagent started.');
    }

    private async runToCompletion(
        run: SubagentRun,
        opening: MessageContent,
        registry: SubagentRegistry,
        context: ToolExecutionContext,
        type: SubagentType,
    ): Promise<EnhancedToolResult> {
        const wasKilled = () => registry.getRun(run.id)?.killRequested === true;
        const killedResult = failure(
            'A developer stopped this subagent before it finished. Any edits it had already made to the video remain.',
        );

        await run.conversation.addUserMessage(opening);

        // Cancelling a conversation only aborts a turn already in flight, and the child's first
        // turn hasn't started yet, so a stop that landed during the await above is caught here.
        if (wasKilled()) {
            return killedResult;
        }
        if (context.abortSignal?.aborted) {
            return failure('Cancelled before the subagent started.');
        }

        const result = await run.conversation.executeWithLLM({
            parentActionGroup: context.parentActionGroup,
        });

        if (wasKilled()) {
            return killedResult;
        }
        // A designed scene's outcome is its last write, which a capped retry can leave behind a
        // failed run.
        const designed = designsOneScene(type) ? designOutcome(run.conversation, type) : null;
        if (designed) {
            return designed;
        }
        if (!result.success) {
            return failure(
                `The subagent failed: ${result.error ?? result.message ?? 'unknown error'}`,
            );
        }
        return {
            result: result.message || 'The subagent finished without writing a report.',
            isSuccess: true,
        };
    }
}

/**
 * A batched call's own part of the diff: its scene groups, or a chart's one scene. A chart never
 * changes scene groups, so any change to one is a sibling's.
 */
function ownPartOf(
    diff: VideoDiff,
    startSnapshot: VideoStateSnapshot,
    currentSnapshot: VideoStateSnapshot,
    claim: Claim,
): VideoDiff {
    if (claim.sceneIds.length > 0) {
        const withoutGroups: VideoDiff = {
            ...diff,
            changes: {
                ...diff.changes,
                sceneGroups: { added: [], removed: [], modified: [] },
            },
        };
        return filterVideoDiffToScenes(withoutGroups, (sceneId) =>
            claim.sceneIds.includes(sceneId),
        );
    }
    return filterVideoDiffToSceneGroups(
        diff,
        startSnapshot,
        currentSnapshot,
        new Set(claim.sceneGroupIds),
    );
}

/** A chart scene that already has a design: a revision, not a new chart. */
function isDesignedChartScene(scene: Scene): boolean {
    return (scene.getAssetsByType(VideoData.HTML_ASSET_TYPE) as HtmlAsset[]).some(
        (asset) => asset.html.trim() !== '',
    );
}

/**
 * What a chart or graphic run left on its scene, from its last write: designed, or still failing
 * its checks, with why. Null when it never wrote, so the run's own failure (or a missing write) is
 * reported.
 */
function designOutcome(
    conversation: Conversation,
    type: 'chart' | 'graphic',
): EnhancedToolResult | null {
    const messages = conversation.getMessages();
    const writeIds = new Set(
        messages.flatMap((message) =>
            isAssistantMessage(message)
                ? (message.tool_calls ?? [])
                      .filter((call) => call.function.name === TOOL_NAMES.writeSceneHtml)
                      .map((call) => call.id)
                : [],
        ),
    );
    const lastWrite = messages
        .filter(
            (message) =>
                isToolMessage(message) &&
                writeIds.has(message.tool_call_id) &&
                message.content !== SKIPPED_AT_CAP_RESULT,
        )
        .at(-1);
    if (!lastWrite || !isToolMessage(lastWrite)) {
        return writeIds.size === 0
            ? failure(`The ${type} subagent replied without writing the scene.`)
            : null;
    }
    if (lastWrite.isSuccess) {
        return { result: `Designed ${DESIGNED_WORK[type]}.`, isSuccess: true };
    }
    const reason = typeof lastWrite.content === 'string' ? lastWrite.content : '';
    return failure(
        `The ${type} couldn't be designed, so the scene keeps the design it had: ${reason}`,
    );
}
